import React, { createContext, useContext, useReducer, ReactNode } from 'react';

// Create a context to hold the state
const WizardContext = createContext<{ step: number; data: any; dispatch: React.Dispatch<any>;}>({
  step: 0,
  data: {},
  dispatch: () => null,
});

// Define the initial state
const initialState = {
  step: 0,
  data: {},
};

// Define action types as an enum
export enum WizardFlowActionType {
  NEXT_STEP = 'next_step',
  PREV_STEP = 'prev_step',
  SET_STEP = 'set_step',
  SET_DATA = 'set_data',
}

/**
 * Reducer function to manage the state of a wizard with steps and data.
 *
 * @param state - The current state of the wizard, containing the current step and data.
 * @param action - The action to be performed on the state, containing a type and an optional payload.
 *
 * @returns The new state of the wizard after applying the action.
 *
 * @throws Will throw an error if the action type is not recognized.
 *
 * Action types:
 * - 'next_step': Advances the wizard to the next step.
 * - 'prev_step': Moves the wizard back to the previous step.
 * - 'set_step': Sets the wizard to a specific step, provided in the payload.
 * - 'set_data': Merges new data into the current state data, provided in the payload.
 *
 */
const reducer = (state: { step: number; data: any }, action: { type: WizardFlowActionType; payload?: any }) => {
  switch (action.type) {
    case WizardFlowActionType.NEXT_STEP:
      return { ...state, step: state.step + 1 };
    case WizardFlowActionType.PREV_STEP:
      return { ...state, step: state.step - 1 };
    case WizardFlowActionType.SET_STEP:
      return { ...state, step: action.payload ?? state.step };
    case WizardFlowActionType.SET_DATA:
      return { ...state, data: { ...state.data, ...action.payload } };
    default:
      throw new Error('Unknown action type');
  }
};

export const WizardProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const value = React.useMemo(() => ({ ...state, dispatch }), [state, dispatch]);
  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
};

export const useWizardContext = () => useContext(WizardContext);
